import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import { ventasService } from '../../services/ventasService';
import { facturasService } from '../../services/facturasService';
import { sucursalesService } from '../../services/sucursalesService';
import { empresasService } from '../../services/empresasService';
import FacturaDetalle from './FacturaDetalle';
import GenerarFacturaModal from './GenerarFacturaModal';
import ConfigurarPrefacturaModal from './ConfigurarPrefacturaModal';
import SnowFallComponent from 'components/SnowFallComponent';

function Facturas() {
  const [ventas, setVentas] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDetalle, setShowDetalle] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [showGenerarModal, setShowGenerarModal] = useState(false);
  const [showConfigurarPrefacturaModal, setShowConfigurarPrefacturaModal] = useState(false);
  const [ventaParaFacturar, setVentaParaFacturar] = useState(null);
  const [clienteParaFacturar, setClienteParaFacturar] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [facturaACancelar, setFacturaACancelar] = useState(null);
  const [motivoCodigo, setMotivoCodigo] = useState('');
  const [folioSustitucion, setFolioSustitucion] = useState('');
  const [cancelando, setCancelando] = useState(false);

  // Catálogo de motivos de cancelación según SAT
  const MOTIVOS_CANCELACION = [
    { value: '01', label: '01 - Comprobante emitido con errores con relación' },
    { value: '02', label: '02 - Comprobante emitido con errores sin relación' },
    { value: '03', label: '03 - No se llevó a cabo la operación' },
    { value: '04', label: '04 - Operación nominativa relacionada en la factura global' },
  ];
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const loadingVentasRef = useRef(false);

  useEffect(() => {
    loadEmpresaYSucursales();
  }, []);

  const loadVentas = async () => {
    if (!sucursalSeleccionada) return;
    
    // Evitar cargas múltiples simultáneas
    if (loadingVentasRef.current) return;
    
    try {
      loadingVentasRef.current = true;
      setLoading(true);
      setError('');
      const sucursalId = parseInt(sucursalSeleccionada);
      if (!sucursalId) return;

      const response = await ventasService.getAll(
        sucursalId,
        null,
        filtroFechaDesde || null,
        filtroFechaHasta || null,
        filtroEstatus || null
      );

      if (response.success && response.data) {
        const ventasData = Array.isArray(response.data) ? response.data : [];
        // Cargar facturas para cada venta
        const ventasConFacturas = await Promise.all(
          ventasData.map(async (venta) => {
            try {
              const facturaResponse = await facturasService.getByVenta(venta.VentaID || venta.ventaID);
              return {
                ...venta,
                tieneFactura: facturaResponse.success && facturaResponse.data,
                factura: facturaResponse.success ? facturaResponse.data : null,
              };
            } catch {
              return {
                ...venta,
                tieneFactura: false,
                factura: null,
              };
            }
          })
        );

        const normalized = ventasConFacturas.map(venta => ({
          VentaID: venta.VentaID || venta.ventaID,
          SucursalID: venta.SucursalID || venta.sucursalID,
          SucursalNombre: venta.SucursalNombre || venta.sucursalNombre || '',
          ClienteID: venta.ClienteID || venta.clienteID,
          ClienteNombre: venta.ClienteNombre || venta.clienteNombre,
          UsuarioID: venta.UsuarioID || venta.usuarioID,
          UsuarioNombre: venta.UsuarioNombre || venta.usuarioNombre || '',
          Folio: venta.Folio || venta.folio,
          Subtotal: venta.Subtotal !== undefined ? parseFloat(venta.Subtotal) : (venta.subtotal !== undefined ? parseFloat(venta.subtotal) : 0),
          DescuentoTotal: venta.DescuentoTotal !== undefined ? parseFloat(venta.DescuentoTotal) : (venta.descuentoTotal !== undefined ? parseFloat(venta.descuentoTotal) : 0),
          Impuestos: venta.Impuestos !== undefined ? parseFloat(venta.Impuestos) : (venta.impuestos !== undefined ? parseFloat(venta.impuestos) : 0),
          Total: venta.Total !== undefined ? parseFloat(venta.Total) : (venta.total !== undefined ? parseFloat(venta.total) : 0),
          Estatus: venta.Estatus || venta.estatus || '',
          FechaVenta: venta.FechaVenta || venta.fechaVenta,
          Observaciones: venta.Observaciones || venta.observaciones,
          tieneFactura: venta.tieneFactura,
          factura: venta.factura,
        }));
        setVentas(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar ventas');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
      loadingVentasRef.current = false;
    }
  };

  useEffect(() => {
    if (!loadingSucursales && sucursalSeleccionada) {
      loadVentas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSucursales, sucursalSeleccionada, filtroFechaDesde, filtroFechaHasta, filtroEstatus]);

  // Verificar si hay un ID en sessionStorage para abrir el detalle automáticamente
  useEffect(() => {
    const openDetailId = sessionStorage.getItem('openDetail_factura');
    if (openDetailId && !loading && sucursalSeleccionada) {
      // Cargar la factura específica desde la API para asegurar que esté disponible
      // independientemente del filtro de sucursal
      const loadFacturaFromAPI = async () => {
        try {
          const response = await facturasService.getById(openDetailId);
          if (response.success && response.data) {
            sessionStorage.removeItem('openDetail_factura');
            const factura = response.data;
            // Buscar la venta asociada en la lista actual o cargarla si no está
            const ventaId = factura.VentaID || factura.ventaID;
            if (ventaId) {
              let ventaConFactura = ventas.find(v => 
                (v.VentaID || v.ventaID)?.toString() === ventaId.toString()
              );
              
              // Si no está en la lista, cargar la venta desde la API
              if (!ventaConFactura) {
                try {
                  const ventaResponse = await ventasService.getById(ventaId);
                  if (ventaResponse.success && ventaResponse.data) {
                    ventaConFactura = ventaResponse.data;
                    ventaConFactura.factura = factura;
                  }
                } catch (err) {
                  // Si no se puede cargar la venta, crear un objeto mínimo
                  ventaConFactura = {
                    VentaID: ventaId,
                    ventaID: ventaId,
                    factura: factura,
                  };
                }
              } else {
                // Si está en la lista, actualizar la factura
                ventaConFactura.factura = factura;
              }
              
              if (ventaConFactura) {
                handleViewFactura(ventaConFactura);
              }
            }
          } else {
            // Si no se encuentra en la API, intentar buscarla en la lista actual
            sessionStorage.removeItem('openDetail_factura');
            const ventaConFactura = ventas.find(v => {
              if (v.factura) {
                const facturaId = v.factura.FacturaID || v.factura.facturaID;
                return facturaId?.toString() === openDetailId;
              }
              return false;
            });
            if (ventaConFactura && ventaConFactura.factura) {
              handleViewFactura(ventaConFactura);
            }
          }
        } catch (error) {
          // Si hay error, intentar buscarla en la lista actual como fallback
          sessionStorage.removeItem('openDetail_factura');
          const ventaConFactura = ventas.find(v => {
            if (v.factura) {
              const facturaId = v.factura.FacturaID || v.factura.facturaID;
              return facturaId?.toString() === openDetailId;
            }
            return false;
          });
          if (ventaConFactura && ventaConFactura.factura) {
            handleViewFactura(ventaConFactura);
          }
        }
      };
      
      loadFacturaFromAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, ventas, sucursalSeleccionada]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          // Solo actualizar si el ID cambió para evitar re-renders innecesarios
          const empresaId = empresaActiva.EmpresaID || empresaActiva.empresaID;
          if (!empresa || (empresa.EmpresaID || empresa.empresaID) !== empresaId) {
            setEmpresa(empresaActiva);
          }
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            // Solo establecer sucursal seleccionada si no hay una ya seleccionada
            if (normalized.length > 0 && !sucursalSeleccionada) {
              setSucursalSeleccionada(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };


  const handleGenerarFactura = async (venta) => {
    // Verificar si ya existe una prefactura
    try {
      const response = await facturasService.getByVenta(venta.VentaID || venta.ventaID);
      if (response.success && response.data) {
        const factura = response.data;
        const estatus = (factura.Estatus || factura.estatus || '').toLowerCase();
        
        if (estatus === 'borrador' || estatus === 'pendiente') {
          // Ya existe una prefactura, abrir modal de configuración
          setVentaParaFacturar(venta);
          setClienteParaFacturar(venta.Cliente || venta.cliente || null);
          setShowConfigurarPrefacturaModal(true);
          return;
        } else if (estatus === 'timbrada') {
          // Ya está timbrada, mostrar detalle
          setFacturaSeleccionada(factura);
          setShowDetalle(true);
          return;
        }
      }
    } catch (err) {
    }
    
    // No existe prefactura o hay error, crear nueva
    setVentaParaFacturar(venta);
    setClienteParaFacturar(venta.Cliente || venta.cliente || null);
    setShowConfigurarPrefacturaModal(true);
  };

  const handleViewFactura = async (venta) => {
    try {
      setLoading(true);
      const response = await facturasService.getByVenta(venta.VentaID || venta.ventaID);
      if (response.success && response.data) {
        setFacturaSeleccionada(response.data);
        setShowDetalle(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar la factura');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleReintentarTimbrado = async (factura) => {
    try {
      setLoading(true);
      const response = await facturasService.reintentarTimbrado(factura.FacturaID || factura.facturaID);
      if (response.success) {
        setSuccess('Factura marcada para reintentar timbrado');
        setTimeout(() => setSuccess(''), 3000);
        loadVentas();
      } else {
        setError(response.Message || response.message || 'Error al reintentar timbrado');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (factura) => {
    setFacturaACancelar(factura);
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!motivoCodigo) {
      setError('El motivo de cancelación es requerido');
      return;
    }

    // Si el motivo es 01, el folio de sustitución es obligatorio
    if (motivoCodigo === '01' && !folioSustitucion.trim()) {
      setError('El folio de factura sustituta es requerido cuando el motivo es 01');
      return;
    }

    try {
      setCancelando(true);
      const facturaId = facturaACancelar.FacturaID || facturaACancelar.facturaID;

      const cancelacionData = {
        MotivoCodigo: motivoCodigo,
      };

      // Solo incluir folio de sustitución si tiene valor
      if (folioSustitucion.trim()) {
        cancelacionData.FolioSustitucion = folioSustitucion.trim();
      }

      const response = await facturasService.cancelar(facturaId, cancelacionData);

      if (response.success) {
        setSuccess('Factura cancelada exitosamente');
        setTimeout(() => setSuccess(''), 3000);
        setShowCancelModal(false);
        setFacturaACancelar(null);
        setMotivoCodigo('');
        setFolioSustitucion('');
        loadVentas();
      } else {
        setError(response.Message || response.message || 'Error al cancelar la factura');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setCancelando(false);
    }
  };

  const handleDescargarXML = async (factura) => {
    try {
      const response = await facturasService.descargarXML(factura.FacturaID || factura.facturaID);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${factura.FacturaID || factura.facturaID}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('XML descargado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al descargar el XML');
    }
  };

  const handleDescargarPDF = async (factura) => {
    try {
      const response = await facturasService.descargarPDF(factura.FacturaID || factura.facturaID);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${factura.FacturaID || factura.facturaID}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('PDF descargado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al descargar el PDF');
    }
  };

  const handleFormSuccess = () => {
    setShowGenerarModal(false);
    setShowConfigurarPrefacturaModal(false);
    setVentaParaFacturar(null);
    setClienteParaFacturar(null);
    setSuccess('Factura procesada exitosamente');
    setTimeout(() => setSuccess(''), 3000);
    loadVentas();
  };

  const handleFormCancel = () => {
    setShowGenerarModal(false);
    setShowConfigurarPrefacturaModal(false);
    setVentaParaFacturar(null);
    setClienteParaFacturar(null);
  };

  const handleDetalleClose = () => {
    setShowDetalle(false);
    setFacturaSeleccionada(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEstatusBadge = (estatus) => {
    const estatusLower = (estatus || '').toLowerCase();
    if (estatusLower === 'timbrada') {
      return <Badge color="success">Timbrada</Badge>;
    } else if (estatusLower === 'pendiente') {
      return <Badge color="warning">Pendiente</Badge>;
    } else if (estatusLower === 'borrador') {
      return <Badge color="info">Borrador</Badge>;
    } else if (estatusLower === 'cancelada') {
      return <Badge color="danger">Cancelada</Badge>;
    } else if (estatusLower === 'error') {
      return <Badge color="danger">Error</Badge>;
    }
    return <Badge color="secondary">{estatus || 'Desconocido'}</Badge>;
  };

  const columns = [
    {
      key: 'folio',
      label: 'Folio Venta',
      accessor: 'Folio',
      render: (value) => value || '-',
    },
    {
      key: 'fechaVenta',
      label: 'Fecha Venta',
      accessor: 'FechaVenta',
      render: (value) => formatDate(value),
    },
    {
      key: 'cliente',
      label: 'Cliente',
      accessor: 'ClienteNombre',
      render: (value) => value || 'Cliente General',
    },
    {
      key: 'total',
      label: 'Total',
      accessor: 'Total',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'factura',
      label: 'Factura',
      render: (_, row) => {
        if (!row.tieneFactura) {
          return <Badge color="secondary">Sin factura</Badge>;
        }
        const factura = row.factura;
        const estatus = factura?.Estatus || factura?.estatus || '';
        return getEstatusBadge(estatus);
      },
    },
    {
      key: 'uuid',
      label: 'UUID',
      render: (_, row) => {
        if (!row.tieneFactura || !row.factura) return '-';
        const uuid = row.factura.UUID || row.factura.uuid;
        return uuid ? (
          <span style={{ fontSize: '0.85em' }}>{uuid.substring(0, 8)}...</span>
        ) : '-';
      },
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => {
        const estatusVenta = (row.Estatus || '').toLowerCase();
        const puedeFacturar = estatusVenta === 'completada' && !row.tieneFactura;
        const factura = row.factura;
        const estatusFactura = factura ? (factura.Estatus || factura.estatus || '').toLowerCase() : '';
        const esBorrador = estatusFactura === 'borrador';
        const puedeReintentar = estatusFactura === 'error' || estatusFactura === 'pendiente';
        const puedeCancelar = estatusFactura === 'timbrada';
        const puedeDescargarXML = estatusFactura === 'timbrada' && (factura?.UUID || factura?.uuid);
        const puedeDescargarPDF = estatusFactura === 'timbrada' && (factura?.UUID || factura?.uuid);

        return (
          <div>
            {(puedeFacturar || esBorrador) && (
              <Button
                color={esBorrador ? "warning" : "primary"}
                size="sm"
                className="mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerarFactura(row);
                }}
                title={esBorrador ? "Configurar Prefactura" : "Crear Prefactura"}
              >
                <i className={esBorrador ? "tim-icons icon-settings" : "tim-icons icon-simple-add"} />
              </Button>
            )}
            {row.tieneFactura && (
              <>
                <Button
                  color="info"
                  size="sm"
                  className="mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewFactura(row);
                  }}
                  title="Ver Factura"
                >
                  <i className="tim-icons icon-zoom-split" />
                </Button>
                {puedeReintentar && (
                  <Button
                    color="warning"
                    size="sm"
                    className="mr-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReintentarTimbrado(factura);
                    }}
                    title="Reintentar Timbrado"
                  >
                    <i className="tim-icons icon-refresh-02" />
                  </Button>
                )}
                {puedeCancelar && (
                  <Button
                    color="danger"
                    size="sm"
                    className="mr-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel(factura);
                    }}
                    title="Cancelar Factura"
                  >
                    <i className="tim-icons icon-simple-remove" />
                  </Button>
                )}
                {puedeDescargarXML && (
                  <Button
                    color="success"
                    size="sm"
                    className="mr-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDescargarXML(factura);
                    }}
                    title="Descargar XML"
                  >
                    <i className="fas fa-file-code" />
                  </Button>
                )}
                {puedeDescargarPDF && (
                  <Button
                    color="info"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDescargarPDF(factura);
                    }}
                    title="Descargar PDF"
                  >
                    <i className="fas fa-file-pdf" />
                  </Button>
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  if (showDetalle && facturaSeleccionada) {
    return (
      <FacturaDetalle
        factura={facturaSeleccionada}
        onClose={handleDetalleClose}
        onCancel={handleCancel}
        onReintentar={handleReintentarTimbrado}
        onDescargarXML={handleDescargarXML}
      />
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Facturación</CardTitle>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="3">
                  <select
                    className="form-control"
                    value={sucursalSeleccionada}
                    onChange={(e) => setSucursalSeleccionada(e.target.value)}
                    disabled={loadingSucursales}
                  >
                    <option value="">Seleccione una sucursal</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md="2">
                  <Input
                    type="date"
                    placeholder="Fecha desde"
                    value={filtroFechaDesde}
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="date"
                    placeholder="Fecha hasta"
                    value={filtroFechaHasta}
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="select"
                    value={filtroEstatus}
                    onChange={(e) => setFiltroEstatus(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="Completada">Completada</option>
                    <option value="Cancelada">Cancelada</option>
                  </Input>
                </Col>
                <Col md="1">
                  <Button color="primary" onClick={loadVentas} disabled={!sucursalSeleccionada}>
                    <i className="tim-icons icon-zoom-split" />
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={ventas} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <GenerarFacturaModal
        isOpen={showGenerarModal}
        toggle={handleFormCancel}
        venta={ventaParaFacturar}
        onSuccess={handleFormSuccess}
      />

      <ConfigurarPrefacturaModal
        isOpen={showConfigurarPrefacturaModal}
        toggle={handleFormCancel}
        venta={ventaParaFacturar}
        cliente={clienteParaFacturar}
        onSuccess={handleFormSuccess}
      />

      <ConfirmModal
        isOpen={showCancelModal}
        toggle={() => {
          setShowCancelModal(false);
          setFacturaACancelar(null);
          setMotivoCodigo('');
          setFolioSustitucion('');
        }}
        title="Cancelar Factura"
        message={
          <div>
            <p>¿Está seguro de cancelar esta factura?</p>
            <div className="mt-3">
              <label className="form-label">Motivo de Cancelación (SAT) *</label>
              <Input
                type="select"
                value={motivoCodigo}
                onChange={(e) => {
                  setMotivoCodigo(e.target.value);
                  // Si cambia a un motivo diferente de 01, limpiar folio de sustitución
                  if (e.target.value !== '01') {
                    setFolioSustitucion('');
                  }
                }}
                required
              >
                <option value="">Seleccione un motivo</option>
                {MOTIVOS_CANCELACION.map((motivo) => (
                  <option key={motivo.value} value={motivo.value}>
                    {motivo.label}
                  </option>
                ))}
              </Input>
            </div>
            {motivoCodigo === '01' && (
              <div className="mt-3">
                <label className="form-label">Folio de Factura Sustituta *</label>
                <Input
                  type="text"
                  placeholder="Ingrese el folio de la factura que sustituye a esta"
                  value={folioSustitucion}
                  onChange={(e) => setFolioSustitucion(e.target.value)}
                  required
                />
                <small className="text-muted">
                  Requerido cuando el motivo es 01 (Comprobante emitido con errores con relación)
                </small>
              </div>
            )}
          </div>
        }
        confirmText="Cancelar Factura"
        cancelText="Cerrar"
        onConfirm={confirmCancel}
        loading={cancelando}
      />
    </div>
  );
}

export default Facturas;

